<script setup>
import { computed } from 'vue'
import StarIcon from './StarIcon.vue'
import StarMarkSVG from './StarMarkSVG.vue'
import ShareStarProductButton from './ShareStarProductButton.vue'

const props = defineProps({
    item:       { type: Object,  required: true },
    purchasing: { type: Boolean, default: false },
    isCopied:   { type: Boolean, default: false },
})

const emit = defineEmits(['contribute', 'share'])

const SUBTYPE_LABELS = { 1: 'Unlock link', 2: 'Get coupon', 3: 'Unlock link' }

// Coupons get their own lime accent; OF links keep cyan.
const isCoupon = computed(() => props.item.discount_subtype === 2)
const accent = computed(() => isCoupon.value ? '#A3E635' : '#67E8F9')

function ctaLabel() {
    if (props.purchasing && props.item.available) return 'Processing…'
    if (!props.item.available) return props.item.total > 0 ? 'Unavailable' : 'Coming soon'
    const verb = SUBTYPE_LABELS[props.item.discount_subtype] ?? 'Get discount'
    return `${verb} · ${props.item.price.toLocaleString()} stars`
}

function pct() {
    return props.item.total ? Math.round(props.item.claimed / props.item.total * 100) : 0
}
</script>

<template>
    <article class="card card--padded">
        <span v-if="item.hot" class="chip card__leading" :class="isCoupon ? 'chip--lime' : 'chip--cyan'">
            <StarIcon name="flame" :size="9" :stroke-width="2.5" /> Hot
        </span>
        <div class="bounty__head">
            <div v-if="item.photo" class="avatar avatar--sm" style="width:44px;height:44px;font-size:15px;background:linear-gradient(135deg,#374151,#4B5563)">
                <img :src="item.photo" :alt="item.name" class="avatar__img" />
            </div>
            <div style="flex:1;min-width:0">
                <div class="bounty__name" style="font-size:15px">{{ item.name }}</div>
                <div class="muted small">{{ item.product_name }}</div>
            </div>
        </div>
        <p v-if="item.product_description" class="muted small" style="margin:0 0 10px">{{ item.product_description }}</p>
        <div v-if="item.off" class="of-offer" :style="{ '--offer-accent': accent }">
            <template v-if="item.off_type === 'months'">
                <span class="of-offer__num">{{ item.off }}</span>
                <span class="of-offer__lbl">free month{{ item.off === 1 ? '' : 's' }}</span>
            </template>
            <template v-else-if="item.off_type === 'days'">
                <span class="of-offer__num">{{ item.off }}</span>
                <span class="of-offer__lbl">free day{{ item.off === 1 ? '' : 's' }}</span>
            </template>
            <template v-else>
                <span class="of-offer__num">{{ item.off_type === '$' ? '$' : '' }}{{ item.off }}{{ item.off_type === '%' ? '%' : '' }}</span>
                <span class="of-offer__lbl">off<template v-if="item.period"><br/>{{ item.period }}</template></span>
            </template>
        </div>
        <div class="card__line">
            <span class="muted">{{ item.claimed }} / {{ item.total }} claimed</span>
            <span :style="{ color: accent }" style="font-weight:700">{{ pct() }}%</span>
        </div>
        <div class="bar">
            <div class="bar__fill" :class="isCoupon ? 'bar__fill--lime' : 'bar__fill--cyan'" :style="{ width: pct() + '%' }" />
        </div>
        <button class="btn w-full" :class="[isCoupon ? 'btn--lime' : 'btn--cyan', { 'btn--loading': purchasing }]" :disabled="!item.available || purchasing" @click="item.available && emit('contribute')">
            <span v-if="purchasing && item.available" class="btn-spinner" style="border-color:rgba(4,34,42,0.3);border-top-color:currentColor" />
            <StarMarkSVG v-else-if="item.available" :size="11" />
            {{ ctaLabel() }}
        </button>
        <ShareStarProductButton :copied="isCopied" @share="emit('share')" />
    </article>
</template>

<style scoped>
.muted { color: rgba(255,255,255,0.68); }
.small { font-size: 11px; }

.chip {
    font-size: 9px; font-weight: 900; letter-spacing: 0.10em; text-transform: uppercase;
    padding: 3px 8px; border-radius: 9999px;
    display: inline-flex; align-items: center; gap: 4px;
}
.chip--cyan { background: rgba(34,211,238,0.15); color: #67E8F9; border: 1px solid rgba(103,232,249,0.40); }
.chip--lime { background: rgba(163,230,53,0.15); color: #A3E635; border: 1px solid rgba(163,230,53,0.40); }
/* right offsets clear the corner share button */
.card__leading { position: absolute; top: 10px; right: 42px; z-index: 2; }

.bounty__head { display: flex; align-items: center; gap: 14px; margin-bottom: 14px; padding-right: 26px; }
.bounty__name { font-family: 'Manrope', sans-serif; font-size: 18px; font-weight: 800; letter-spacing: -0.01em; margin-bottom: 3px; }

.avatar {
    width: 64px; height: 64px; border-radius: 50%;
    display: inline-flex; align-items: center; justify-content: center;
    font-family: 'Manrope', sans-serif; font-size: 22px; font-weight: 900; color: #fff;
    flex-shrink: 0; overflow: hidden; position: relative;
}
.avatar__img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; object-position: top center; }
.avatar--sm { width: 36px; height: 36px; font-size: 13px; }

.of-offer { display: flex; align-items: center; gap: 10px; position: relative; margin-bottom: 14px; }
.of-offer__num { font-family: 'Manrope', sans-serif; font-size: 40px; font-weight: 900; letter-spacing: -0.03em; line-height: 1; color: var(--offer-accent, #67E8F9); }
.of-offer__lbl { font-size: 11px; line-height: 1.25; color: rgba(255,255,255,0.68); text-transform: uppercase; letter-spacing: 0.06em; font-weight: 700; }

.card__line { display: flex; justify-content: space-between; font-size: 11px; margin-bottom: 5px; font-variant-numeric: tabular-nums; }
.card--padded > .card__line { margin-top: auto; }

.bar { height: 6px; background: rgba(255,255,255,0.10); border-radius: 9999px; overflow: hidden; margin-bottom: 12px; }
.bar__fill { height: 100%; }
.bar__fill--cyan { background: linear-gradient(90deg,#0891B2,#67E8F9); }
.bar__fill--lime { background: linear-gradient(90deg,#4D7C0F,#A3E635); }

.btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    padding: 12px 18px; border-radius: 10px; border: none; cursor: pointer;
    font-size: 14px; font-weight: 700; font-family: inherit;
    transition: transform 120ms ease;
    color: #fff;
}
.btn:active { transform: scale(0.985); }
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn--loading { opacity: 0.80; }
.btn--cyan { background: linear-gradient(135deg,#22D3EE,#0891B2); color: #04222a; font-weight: 800; box-shadow: 0 8px 20px rgba(34,211,238,0.22); }
.btn--lime { background: linear-gradient(135deg,#A3E635,#65A30D); color: #1a2e05; font-weight: 800; box-shadow: 0 8px 20px rgba(163,230,53,0.22); }
.w-full { width: 100%; }

.btn-spinner {
    display: inline-block; width: 14px; height: 14px;
    border: 2px solid rgba(255,255,255,0.25);
    border-top-color: currentColor;
    border-radius: 50%;
    animation: btn-spin 0.65s linear infinite;
    flex-shrink: 0;
}
@keyframes btn-spin { to { transform: rotate(360deg); } }
</style>
